import moment from 'moment';
import {
	AssignmentSource,
	AssignmentSourceActivity,
	AssignmentSourceLearningPath
} from 'Services/Api/AssignmentSources/Types';
import { LearningPath } from 'Services/Api/LearningManager/Types';

/**
 * Resolves the course id an assignment-source activity points at.
 *
 * Responses return the reference as `id` while create/update payloads use
 * `activity_id`, so read through this instead of picking one key.
 */
export const assignmentActivityId = (
	activity: AssignmentSourceActivity
): number | undefined => activity.activity_id ?? activity.id;

/** Course ids referenced by an assignment source's activities, deduped. */
export const assignmentActivityIds = (
	activities: AssignmentSourceActivity[]
): number[] => [
	...new Set(
		activities
			.map(assignmentActivityId)
			.filter((id): id is number => typeof id === 'number')
	)
];

export const assignmentLearningPathId = (
	learningPath: AssignmentSourceLearningPath
): number | undefined => learningPath.learning_path_id ?? learningPath.id;

export const assignmentLearningPathIds = (
	learningPaths: AssignmentSourceLearningPath[]
): number[] => [
	...new Set(
		learningPaths
			.map(assignmentLearningPathId)
			.filter((id): id is number => typeof id === 'number')
	)
];

type Expiring = Pick<
	AssignmentSource,
	| 'expiration_type'
	| 'expiration_interval_value'
	| 'expiration_interval_unit'
	| 'expiration_date'
>;

export const certificationLabel = (entity?: Expiring): string => {
	if (!entity) return '';
	if (
		entity.expiration_type === 'interval' &&
		entity.expiration_interval_value
	) {
		const unit = entity.expiration_interval_unit ?? '';
		const plural = entity.expiration_interval_value > 1 ? 's' : '';
		return `${entity.expiration_interval_value} ${unit}${plural}`;
	}
	if (entity.expiration_type === 'date' && entity.expiration_date) {
		const date = moment(entity.expiration_date, 'MM-DD', true);
		return date.isValid() ? date.format('M/D') : entity.expiration_date;
	}
	return 'Never expires';
};

export const assignmentsForCourse = (
	sources: AssignmentSource[],
	learningPathsById: Record<number, LearningPath>,
	courseId: number
): AssignmentSource[] =>
	sources.filter((source) => {
		if (assignmentActivityIds(source.activities).includes(courseId)) {
			return true;
		}
		return assignmentLearningPathIds(source.learning_paths).some((pathId) =>
			(learningPathsById[pathId]?.activities ?? []).some(
				(activity) => activity.activity_id === courseId
			)
		);
	});
